import { Link } from 'react-router-dom';
import { Cpu } from 'lucide-react';
import { api } from '@/lib/api';
import { useApi } from '@/hooks/useApi';
import { fechaCorta, TIPO_LABEL } from '@/lib/dispositivos';
import { ChipBateria, ChipConexion } from './Chips';
import { useCatalogosDispositivos } from './catalogos';

/**
 * Lo instalado en una ficha de Propiedades: cada aparato con su conexión, su
 * batería y sus incidencias recientes, y enlace a su ficha. La propiedad del
 * CRM se resuelve a su listing de Hostify en el servidor.
 */
export default function TarjetaDispositivosPropiedad({ propertyId }: { propertyId: string }) {
  const { catalogos } = useCatalogosDispositivos();
  const { data, loading, error } = useApi(() => api.getDispositivosPorPropiedad(propertyId), [propertyId]);

  return (
    <div className="card space-y-3">
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-semibold text-sereno-dark/60 uppercase tracking-wider">Dispositivos</h3>
        {data?.listing && (
          <Link to={`/dispositivos?listingId=${data.listing.id}`} className="text-xs text-sereno-dark/50 hover:text-sereno-dark">
            Ver en el inventario
          </Link>
        )}
      </div>

      {loading ? (
        <p className="text-sm text-sereno-dark/40">Cargando…</p>
      ) : error ? (
        <p className="text-sm text-red-600">{error}</p>
      ) : !data?.listing ? (
        <p className="text-sm text-sereno-dark/40">Esta ficha no está enlazada con ningún listing activo de Hostify.</p>
      ) : data.dispositivos.length === 0 ? (
        <p className="text-sm text-sereno-dark/40">
          Esta casa no tiene dispositivos registrados.{' '}
          <Link to="/dispositivos" className="underline">Instala uno desde el inventario.</Link>
        </p>
      ) : (
        <ul className="divide-y divide-gray-50">
          {data.dispositivos.map((d) => {
            const inst = d.instalaciones.find((i) => i.listingId === data.listing?.id);
            return (
              <li key={d.id} className="py-2 flex items-center gap-3">
                <Cpu size={15} className="text-sereno-dark/40 flex-shrink-0" />
                <div className="flex-1 min-w-0">
                  <Link to={`/dispositivos/${d.id}`} className="text-sm font-medium hover:underline">
                    {TIPO_LABEL[d.tipo]}
                    {d.modelo && catalogos.modeloLabel[d.modelo] ? ` · ${catalogos.modeloLabel[d.modelo]}` : ''}
                  </Link>
                  <p className="text-xs text-sereno-dark/50 truncate">
                    <span className="font-mono">{d.idFabricante}</span>
                    {d.alias ? ` · ${d.alias}` : ''}
                    {inst ? ` · desde ${fechaCorta(inst.desde)}` : ''}
                    {inst?.rol === 'smartlock2' ? ' · segunda smartlock' : ''}
                  </p>
                </div>
                <div className="flex items-center gap-2 flex-shrink-0">
                  <ChipConexion snapshot={d.snapshot} />
                  <ChipBateria snapshot={d.snapshot} />
                  {d.resumen.incidencias90d > 0 && (
                    <span className="text-xs text-red-700 font-medium" title="Incidencias en los últimos 90 días">
                      {d.resumen.incidencias90d} inc.
                    </span>
                  )}
                  {inst?.hostifyPendiente && <span className="text-[10px] text-amber-700 bg-amber-100 px-1.5 py-0.5 rounded">Hostify pendiente</span>}
                </div>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
